import {useMemo} from 'react';
import dayjs from 'dayjs';
import {BorderInfo, CellSpanInfo} from '../../../../../components';
import {
  getPeriodWithPadding,
  getStepsWithPadding,
  isDateInPeriod,
  ScheduleDataContextType,
  ShiftWithTasks,
  isTimeframeOverlappingPeriod,
  getDailyWorkload,
  getTeamMemberWorkload,
  getDailyUnassignedTasks,
  AbsenceInfo,
  getWeekNumber,
} from '../../../../../services';
import {query} from '@di-nxt/query';
import {CalenderRowInfo, CalendarCellInfo} from '../types';

/**
 * Fills in data for the sheet in correct rows and cells.
 *
 * The data model returns a list of cells representing the
 * data to be used for each cell, and a list of cell spans
 * representing data for each row (cell spans)
 */
export const useScheduleSheetData = (context: ScheduleDataContextType) => {
  // Calulate date headers
  const dates = useMemo(() => {
    return getStepsWithPadding(context.date, context.calendarType);
  }, [context.date, context.calendarType]);

  const borderInfo = useMemo((): Array<BorderInfo | undefined> => {
    if (context.calendarType === 'month') {
      const retVal: string[] = ['none', 'border'];
      return retVal.concat(
        dates.map(d =>
          // TODO: lør|søn eller søn|man
          dayjs(d).day() === 0 ? 'vertical-separator' : 'border',
        ),
      ) as Array<BorderInfo | undefined>;
    } else {
      return ['none', 'border'];
    }
  }, [context.calendarType, dates]);

  const {cellInfos, cellSpanInfos} = useMemo(() => {
    // Filter out only those tasks and shifts we want to display
    const {fromDateTime, toDateTime} = getPeriodWithPadding(
      context.date,
      context.calendarType,
    );

    const shiftsForDateTimePeriod = context.shiftsWithTasks.filter(st =>
      isTimeframeOverlappingPeriod(
        st.shift.startTime,
        st.shift.durationMinutes,
        fromDateTime,
        toDateTime,
      ),
    );

    // Let's create cell/row data - start with two empty cells in header
    const cellInfos: CalenderRowInfo[] = [];
    const cellSpanInfos: Array<
      undefined | CellSpanInfo<ShiftWithTasks | AbsenceInfo>[]
    > = [];

    // Fill in header information
    const headerRow: Array<CalendarCellInfo> = [
      {row: '0', col: '0', data: undefined},
      {row: '0', col: '1', data: getWeekNumber(dates[0])},
    ];

    dates.forEach((date, index) => {
      switch (context.calendarType) {
        case 'day':
          // We can provide only the numeric hour for this cell
          headerRow.push({
            row: '0',
            col: index + 2,
            data: {
              date,
              workload: undefined,
              unassignedTasks: getDailyUnassignedTasks(date, context).length,
              enabled: isDateInPeriod(date, context.date, context.calendarType),
            },
          });
          break;
        case 'week':
        case 'month':
          // We need both date and workload
          headerRow.push({
            row: '0',
            col: index + 2,
            data: {
              date,
              workload: getDailyWorkload(date, context),
              unassignedTasks: getDailyUnassignedTasks(date, context).length,
              enabled: isDateInPeriod(date, context.date, context.calendarType),
            },
          });
          break;
      }
    });

    cellInfos.push(headerRow);

    // Add empty header for cell span infos
    cellSpanInfos.push(undefined);

    // Enumerate members and fill in each row
    context.members.forEach((member, rowIndex) => {
      const row: Array<CalendarCellInfo> = [];
      // Fill in member information for the first two cells
      const memberAvatar: CalendarCellInfo = {
        row: rowIndex + 1,
        col: '0',
        data: {
          workloadHours: getTeamMemberWorkload(
            member.membershipId,
            context.date,
            context,
          ),
          member,
        },
      };
      const memberInfo: CalendarCellInfo = {
        row: rowIndex + 1,
        col: '1',
        data: {
          workloadHours: getTeamMemberWorkload(
            member.membershipId,
            context.date,
            context,
          ),
          member,
        },
      };

      row.push(memberAvatar);
      row.push(memberInfo);

      // For the rest we'll be starting on column 2
      const startColumn = 2;

      // Get tasks for member
      const shiftsAndTasksForMember = shiftsForDateTimePeriod.filter(
        st => st.shift.membershipId === member.membershipId,
      );

      const absencesForMember = context.absences.filter(
        p => p.absence.membershipId === member.membershipId,
      );

      // Push empty cells - we'll render all shifts and tasks as cell spans
      // to make sure absence can be displayed at the same time.
      dates.forEach((_, colIndex) => {
        row.push({
          row: rowIndex,
          col: colIndex + startColumn,
          data: undefined,
        });
      });

      // Push row
      cellInfos.push(row);

      // Push cell spans when calendar type is day
      const startDate = dates[0];

      // Push Shifts with tasks
      let infos: CellSpanInfo<ShiftWithTasks | AbsenceInfo>[] = [];

      // Add cellspans for shifts in day mode
      infos.push(
        ...query(shiftsAndTasksForMember)
          .orderBy(st => dayjs(st.shift.startTime).unix())
          .select(st => {
            const startDiff =
              dayjs(st.shift.startTime).diff(startDate, 'minutes') /
              (context.calendarType === 'day' ? 60 : 60 * 24);
            const retval = {
              start:
                context.calendarType === 'day'
                  ? startColumn + startDiff
                  : Math.round(startColumn + startDiff),
              length:
                context.calendarType === 'day'
                  ? Math.max(st.totalTasksHours, st.shift.durationMinutes / 60)
                  : context.calendarType === 'week'
                  ? 1
                  : 1,
              data: st,
            };
            return retval;
          })
          .toArray(),
      );

      // Push absences
      infos.push(
        ...query(absencesForMember)
          .orderBy(afm => dayjs(afm.absence.fromDate).unix())
          .select(absenceInfo => {
            const startDiff =
              dayjs(absenceInfo.absence.fromDate).diff(startDate, 'minutes') /
              (context.calendarType === 'day' ? 60 : 60 * 24);
            const endDiff =
              dayjs(absenceInfo.absence.toDate)
                .hour(23)
                .minute(59)
                .second(59)
                .diff(dayjs(absenceInfo.absence.fromDate), 'seconds') /
              (context.calendarType === 'day' ? 60 * 60 : 60 * 60 * 24);
            const retval = {
              start: startColumn + startDiff,
              length: endDiff,
              data: absenceInfo,
            };
            return retval;
          })
          .toArray(),
      );

      // Push all cell span infos to the current row
      cellSpanInfos.push(infos);
    });

    return {cellInfos, cellSpanInfos};
  }, [context, dates]);

  return {
    cellInfos,
    cellSpanInfos,
    borderInfo,
  };
};
